import {
  Button,
  Modal,
  SegmentedControl,
  StatusPill,
  formatCurrency,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX2 } from 'lucide-react';
import { useState } from 'react';
import { RequestAlert } from '../../app/data-view.js';
import { SectionCardHeader } from '../../app/section-card-header.js';
import { getApiError, schedulingApi, type PortalRefundMethod } from '../../lib/api.js';

const REFUND_METHODS: Record<PortalRefundMethod, string> = {
  CASH: 'Dinheiro',
  PIX: 'Pix',
  OTHER: 'Outro meio',
};

const STATUS = {
  DONE: { tone: 'positive', label: 'Processamento concluído' },
  REQUIRES_ACTION: { tone: 'danger', label: 'Aguarda a empresa' },
  REFUND_FAILED: { tone: 'danger', label: 'Devolução falhou' },
  PENDING: { tone: 'warning', label: 'Em processamento' },
} as const;

/// O que aconteceu com o dinheiro quando o cliente cancelou pelo portal: quanto tinha pago, quanto
/// ficou com a empresa, quanto volta e se o processamento já terminou ou espera a empresa. Quando
/// a devolução falha no provedor, quem gerencia os pagamentos das vendas decide o próximo passo.
export function PortalCancellationCard({
  appointmentId,
  canDecideRefund = false,
}: {
  appointmentId: string;
  canDecideRefund?: boolean;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [dialog, setDialog] = useState(false);
  const [method, setMethod] = useState<PortalRefundMethod>('PIX');
  const [busy, setBusy] = useState<'retry' | 'manual' | null>(null);
  const [error, setError] = useState<string>();
  const queryKey = ['company', 'scheduling', 'portal-cancellation', appointmentId];
  const result = useQuery({
    queryKey,
    queryFn: () => schedulingApi.portalCancellation(appointmentId),
    retry: false,
    refetchInterval: 15000,
  });
  if (result.isLoading) return <p role="status">Consultando o cancelamento…</p>;
  if (result.error)
    return <p role="alert">Não foi possível consultar o cancelamento feito pelo portal.</p>;
  const item = result.data;
  if (!item) return null;
  const money = (cents: number) => formatCurrency(cents, item.currency);
  const destination =
    item.disposition === 'CREDIT' ? 'Vira crédito do cliente' : 'Devolução ao cliente';
  const status = STATUS[item.status];

  async function decide(choice: PortalRefundMethod | null) {
    setBusy(choice ? 'manual' : 'retry');
    setError(undefined);
    try {
      await schedulingApi.decidePortalRefund(appointmentId, choice);
      await queryClient.invalidateQueries({ queryKey });
      // A venda do atendimento muda junto; Vendas relê o que já tinha em tela.
      await queryClient.invalidateQueries({ queryKey: ['company', 'sales'] });
      setDialog(false);
      snackbar.success(
        choice
          ? `Devolução por ${REFUND_METHODS[choice]} registrada. A venda foi ajustada.`
          : 'Nova devolução pedida ao provedor. Se ele pedir autorização, aprove no painel dele.',
      );
    } catch (failure) {
      if (choice) setError(getApiError(failure));
      else snackbar.failure(getApiError(failure));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      aria-label="Cancelamento pelo portal"
      className="rounded-2xl border border-surface-border bg-surface-card shadow-soft"
    >
      <SectionCardHeader title="Cancelamento pelo portal" icon={CalendarX2} />
      <div className="grid gap-2 p-4 text-sm">
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 tabular-nums">
          <dt className="text-content-muted">Adiantamento pago</dt>
          <dd>{money(item.paidCents)}</dd>
          <dt className="text-content-muted">Retido pela política</dt>
          <dd>{money(item.retainedCents)}</dd>
          <dt className="text-content-muted">{destination}</dt>
          <dd className="font-semibold">{money(item.returnedCents)}</dd>
          {item.refundMethod ? (
            <>
              <dt className="text-content-muted">Devolvido por outro meio</dt>
              <dd>{REFUND_METHODS[item.refundMethod]}</dd>
            </>
          ) : null}
        </dl>
        {item.error ? <p className="text-content-secondary">{item.error}</p> : null}
        {item.status === 'REFUND_FAILED' ? (
          canDecideRefund ? (
            <>
              <p className="text-content-muted">
                Peça a devolução de novo ao provedor ou, se já devolveu ao cliente de outro jeito,
                registre como foi.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  loading={busy === 'retry'}
                  disabled={busy !== null}
                  onClick={() => void decide(null)}
                >
                  Tentar devolver de novo
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy !== null}
                  onClick={() => {
                    setError(undefined);
                    setDialog(true);
                  }}
                >
                  Devolvi por outro meio
                </Button>
              </div>
            </>
          ) : (
            <p className="text-content-muted">
              Quem gerencia os pagamentos das vendas decide se pede a devolução de novo ou registra
              a devolução feita por outro meio.
            </p>
          )
        ) : null}
        {item.status !== 'DONE' ? (
          <p className="text-xs text-content-muted">
            A venda do atendimento só é ajustada quando o processamento termina; até lá ela segue
            com o valor original.
          </p>
        ) : null}
        <p className="text-xs text-content-faint">Protocolo {item.protocol}</p>
      </div>
      <Modal
        open={dialog}
        onOpenChange={() => setDialog(false)}
        title="Devolvi por outro meio"
        footer={
          <Button loading={busy === 'manual'} onClick={() => void decide(method)}>
            Registrar devolução
          </Button>
        }
      >
        <div className="grid gap-4 text-sm">
          <p>
            Registra que você devolveu {money(item.returnedCents)} ao cliente fora da Cobrança
            online. A venda é ajustada e a saída fica no Financeiro, para conciliar com o extrato da
            conta financeira de onde o dinheiro saiu. O registro fica com o seu nome.
          </p>
          <SegmentedControl
            label="Como a devolução foi feita"
            options={(Object.keys(REFUND_METHODS) as PortalRefundMethod[]).map((value) => ({
              value,
              label: REFUND_METHODS[value],
            }))}
            value={method}
            onChange={setMethod}
          />
          {error ? <RequestAlert message={error} /> : null}
        </div>
      </Modal>
    </section>
  );
}
